/**
 * Filtro por valores, ordenação e visualização salva para a `TabelaExcel`
 * (ADR-0078). Funções puras, parametrizadas pelo catálogo de colunas:
 * coluna = { key, label, valor(linha), bruto?(linha), num?, fixa? }.
 *
 * Mesmas regras da aba Solicitações do Painel de Assistências
 * (`colunasSolicitacoes.js`), que nasceu presa ao catálogo dela.
 */

const VAZIO = "(vazio)";

function textoDe(coluna, linha) {
  const v = coluna.valor(linha);
  if (Array.isArray(v)) return v.length ? v.map(String) : [VAZIO];
  return [v === null || v === undefined || v === "" ? VAZIO : String(v)];
}

/** Passa se, em cada coluna filtrada, ALGUM valor da linha está marcado. */
export function passaNosFiltros(linha, filtros, colunas) {
  return colunas.every((c) => {
    const set = filtros?.[c.key];
    if (!set) return true;
    return textoDe(c, linha).some((v) => set.has(v));
  });
}

/** Opções do funil de uma coluna: valores do que passa nos OUTROS filtros. */
export function opcoesDaColuna(coluna, linhas, filtros, colunas) {
  const outros = { ...(filtros || {}) };
  delete outros[coluna.key];
  const contagem = new Map();
  linhas.forEach((l) => {
    if (!passaNosFiltros(l, outros, colunas)) return;
    textoDe(coluna, l).forEach((v) => contagem.set(v, (contagem.get(v) || 0) + 1));
  });
  (filtros?.[coluna.key] || []).forEach((v) => { if (!contagem.has(v)) contagem.set(v, 0); });
  return [...contagem.entries()]
    .sort((a, b) => a[0].localeCompare(b[0], "pt-BR", { numeric: true }))
    .map(([value, count]) => ({ value, label: value, count }));
}

/** Clique no nome da coluna: A→Z, Z→A, sem ordem. */
export function proximaOrdenacao(atual, key) {
  if (atual?.key !== key) return { key, dir: "asc" };
  return atual.dir === "asc" ? { key, dir: "desc" } : null;
}

function chaveDeOrdem(coluna, linha) {
  if (coluna.bruto) {
    const b = coluna.bruto(linha);
    return b === undefined || b === null ? "" : b;
  }
  const v = coluna.valor(linha);
  if (coluna.num) return v === "" || v === null || v === undefined ? null : Number(v);
  if (Array.isArray(v)) return v.join(", ");
  return v === null || v === undefined ? "" : String(v);
}

/** Vazios sempre no fim, nos dois sentidos (como o Excel). */
export function ordenarLinhas(linhas, ordenacao, colunas) {
  const coluna = ordenacao && colunas.find((c) => c.key === ordenacao.key);
  if (!coluna) return linhas;
  const sinal = ordenacao.dir === "desc" ? -1 : 1;
  const vazio = (v) => v === "" || v === null;
  return [...linhas].sort((a, b) => {
    const x = chaveDeOrdem(coluna, a);
    const y = chaveDeOrdem(coluna, b);
    if (vazio(x) || vazio(y)) return vazio(x) === vazio(y) ? 0 : (vazio(x) ? 1 : -1);
    if (typeof x === "number" && typeof y === "number") return sinal * (x - y);
    return sinal * String(x).localeCompare(String(y), "pt-BR", { numeric: true, sensitivity: "base" });
  });
}

/** Mantém a ordem do catálogo, só chaves que existem, e as fixas sempre. */
export function normalizarColunas(keys, catalogo, padrao) {
  const pedido = new Set(Array.isArray(keys) && keys.length ? keys : padrao);
  const saida = catalogo.filter((c) => c.fixa || pedido.has(c.key)).map((c) => c.key);
  return saida.length ? saida : padrao;
}

export function paraPreferencia({ colunas, filtros, ordenacao, extras = {} }, catalogo, padrao) {
  const f = {};
  Object.entries(filtros || {}).forEach(([k, set]) => { if (set) f[k] = [...set].sort(); });
  const existe = (k) => catalogo.some((c) => c.key === k);
  return {
    v: 1, ...extras,
    colunas: normalizarColunas(colunas, catalogo, padrao),
    filtros: f,
    ordenacao: ordenacao && existe(ordenacao.key) ? { key: ordenacao.key, dir: ordenacao.dir } : null,
  };
}

export function dePreferencia(pref, catalogo, padrao) {
  const existe = (k) => catalogo.some((c) => c.key === k);
  const filtros = {};
  Object.entries(pref?.filtros || {}).forEach(([k, lista]) => {
    if (existe(k) && Array.isArray(lista)) filtros[k] = new Set(lista);
  });
  const o = pref?.ordenacao;
  return {
    colunas: normalizarColunas(pref?.colunas, catalogo, padrao),
    filtros,
    ordenacao: o && existe(o.key) ? { key: o.key, dir: o.dir === "desc" ? "desc" : "asc" } : null,
  };
}
